import api from './api';
import { normalizeLanguage } from '../constants/Languages';
import requestDeduplicator from '../utils/requestDeduplicator';
import { readVatClassification, toVatPayload } from '../utils/vatClassification';

const DEFAULT_CURRENCY = 'EUR';

export const SERVICE_PACKAGE_MAX_QUANTITY = 999;
export const SERVICE_PACKAGE_MAX_EXPIRES_IN_MONTHS = 36;

export const CustomerServicePackageEffectiveStatus = {
  PendingCheckout: 'PendingCheckout',
  Active: 'Active',
  Exhausted: 'Exhausted',
  Expired: 'Expired',
  Canceled: 'Canceled',
  Refunded: 'Refunded',
  CheckoutExpired: 'CheckoutExpired',
};

export const normalizeLocalizedDictionary = (value) => {
  if (!value || typeof value !== 'object') {
    return {};
  }

  return Object.entries(value).reduce((accumulator, [key, entryValue]) => {
    if (typeof entryValue !== 'string') {
      return accumulator;
    }

    accumulator[normalizeLanguage(key)] = entryValue;
    return accumulator;
  }, {});
};

const buildLocalizedPayload = (value) => (
  Object.fromEntries(
    Object.entries(normalizeLocalizedDictionary(value)).map(([language, entryValue]) => [language.toUpperCase(), entryValue.trim()])
  )
);

const normalizeServiceIds = (serviceIds) => (
  Array.isArray(serviceIds)
    ? [...new Set(serviceIds.map((serviceId) => Number.parseInt(serviceId, 10)).filter((serviceId) => Number.isInteger(serviceId) && serviceId > 0))]
    : []
);

const normalizeTeamMemberIds = (teamMemberIds) => (
  Array.isArray(teamMemberIds)
    ? [...new Set(teamMemberIds.map((id) => String(id)).filter(Boolean))]
    : []
);

const parseOptionalInteger = (value) => {
  if (value === '' || value == null) {
    return null;
  }

  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) ? parsed : null;
};

export const buildServicePackagePayload = (servicePackage) => ({
  name: buildLocalizedPayload(servicePackage.name),
  price: Number(servicePackage.price) || 0,
  quantity: parseOptionalInteger(servicePackage.quantity) ?? 0,
  expiresInMonths: parseOptionalInteger(servicePackage.expiresInMonths),
  serviceIds: normalizeServiceIds(servicePackage.serviceIds),
  teamMemberIds: normalizeTeamMemberIds(servicePackage.teamMemberIds),
  ...toVatPayload(servicePackage),
});

export const toFormServicePackage = (servicePackage) => ({
  id: servicePackage?.id ?? null,
  name: normalizeLocalizedDictionary(servicePackage?.name),
  price: Number(servicePackage?.price) || 0,
  currency: servicePackage?.currency || DEFAULT_CURRENCY,
  quantity: servicePackage?.quantity ?? '',
  expiresInMonths: servicePackage?.expiresInMonths ?? '',
  status: servicePackage?.status || 'Active',
  serviceIds: normalizeServiceIds(servicePackage?.serviceIds),
  ...readVatClassification(servicePackage),
  teamMemberIds: normalizeTeamMemberIds(servicePackage?.teamMemberIds),
  activeHolderCount: Number(servicePackage?.activeHolderCount) || 0,
  createdAt: servicePackage?.createdAt ?? null,
});

export const normalizeCustomerServicePackage = (customerPackage) => ({
  id: customerPackage?.id ?? null,
  customerId: customerPackage?.customerId ?? null,
  servicePackageId: customerPackage?.servicePackageId ?? null,
  name: normalizeLocalizedDictionary(customerPackage?.name),
  status: customerPackage?.status ?? null,
  effectiveStatus: customerPackage?.effectiveStatus ?? null,
  paymentChannel: customerPackage?.paymentChannel ?? null,
  quantity: Number(customerPackage?.quantity) || 0,
  usedQuantity: Number(customerPackage?.usedQuantity) || 0,
  remainingQuantity: Number(customerPackage?.remainingQuantity) || 0,
  pricePaid: Number(customerPackage?.pricePaid) || 0,
  currency: customerPackage?.currency || DEFAULT_CURRENCY,
  includedServiceIds: normalizeServiceIds(customerPackage?.includedServiceIds),
  allowedTeamMemberIds: normalizeTeamMemberIds(customerPackage?.allowedTeamMemberIds),
  expiresInMonths: customerPackage?.expiresInMonths ?? null,
  purchasedAtUtc: customerPackage?.purchasedAtUtc ?? null,
  purchaseDate: customerPackage?.purchaseDate ?? null,
  expiresOn: customerPackage?.expiresOn ?? null,
  canceledAtUtc: customerPackage?.canceledAtUtc ?? null,
  refundedAtUtc: customerPackage?.refundedAtUtc ?? null,
});

export const normalizeCustomerServicePackageManagement = (customerPackage) => ({
  ...normalizeCustomerServicePackage(customerPackage),
  customerName: customerPackage?.customerName || '',
  customerEmail: customerPackage?.customerEmail || '',
  canCancel: Boolean(customerPackage?.canCancel),
});

export const normalizeBookingServicePackageEntitlement = (entitlement) => ({
  customerServicePackageId: entitlement?.customerServicePackageId ?? null,
  servicePackageId: entitlement?.servicePackageId ?? null,
  name: normalizeLocalizedDictionary(entitlement?.name),
  quantity: Number(entitlement?.quantity) || 0,
  usedQuantity: Number(entitlement?.usedQuantity) || 0,
  remainingQuantity: Number(entitlement?.remainingQuantity) || 0,
  expiresOn: entitlement?.expiresOn ?? null,
  isExpiredForDate: Boolean(entitlement?.isExpiredForDate),
  canRedeem: Boolean(entitlement?.canRedeem),
  canCoverAllServices: Boolean(entitlement?.canCoverAllServices),
  requiredServiceIds: normalizeServiceIds(entitlement?.requiredServiceIds),
  coveredServiceIds: normalizeServiceIds(entitlement?.coveredServiceIds),
  missingServiceIds: normalizeServiceIds(entitlement?.missingServiceIds),
  allowedTeamMemberIds: normalizeTeamMemberIds(entitlement?.allowedTeamMemberIds),
});

export const isServicePackageActive = (status) => status === 'Active';

export const isCustomerServicePackageUsable = (customerPackage) => (
  customerPackage?.effectiveStatus === CustomerServicePackageEffectiveStatus.Active
);

const withManagementStore = (storeId) => ({
  _authContext: 'management',
  _storeId: storeId,
});

const ServicePackageService = {
  toFormServicePackage,
  isServicePackageActive,

  getServicePackageManagementData: async (storeId) => (
    requestDeduplicator.deduplicate(`getServicePackageManagement-${storeId}`, async () => {
      const [storeResponse, packagesResponse] = await Promise.all([
        api.get(`/stores/${storeId}`),
        api.get('/service-packages', withManagementStore(storeId)),
      ]);

      const storeData = storeResponse.data;
      const supportedLanguages = (storeData?.supportedLanguages || ['en'])
        .map((language) => normalizeLanguage(language))
        .filter((language, index, array) => array.indexOf(language) === index);

      const activeServices = Array.isArray(storeData?.services)
        ? storeData.services
          .filter((service) => !service.disabled)
          .map((service) => ({
            id: service.id,
            name: normalizeLocalizedDictionary(service.name),
            consentFormId: service.consentFormId ?? null,
            priceMode: service.priceMode,
            durationMode: service.durationMode,
          }))
        : [];

      return {
        supportedLanguages: supportedLanguages.length > 0 ? supportedLanguages : ['en'],
        currency: storeData?.currency || DEFAULT_CURRENCY,
        servicePackageCustomerPaymentMode: storeData?.servicePackageCustomerPaymentMode,
        services: activeServices,
        packages: Array.isArray(packagesResponse.data)
          ? packagesResponse.data.map((servicePackage) => toFormServicePackage(servicePackage))
          : [],
      };
    })
  ),

  getServicePackages: async (storeId) => {
    const response = await api.get('/service-packages', withManagementStore(storeId));
    return (Array.isArray(response.data) ? response.data : []).map((servicePackage) => toFormServicePackage(servicePackage));
  },

  createServicePackage: async (storeId, servicePackage) => {
    const response = await api.post('/service-packages', buildServicePackagePayload(servicePackage), withManagementStore(storeId));
    return toFormServicePackage(response.data);
  },

  updateServicePackage: async (storeId, packageId, servicePackage) => {
    const response = await api.put(`/service-packages/${packageId}`, buildServicePackagePayload(servicePackage), withManagementStore(storeId));
    return toFormServicePackage(response.data);
  },

  activateServicePackage: async (storeId, packageId) => {
    await api.post(`/service-packages/${packageId}/activate`, null, withManagementStore(storeId));
  },

  deactivateServicePackage: async (storeId, packageId) => {
    await api.post(`/service-packages/${packageId}/deactivate`, null, withManagementStore(storeId));
  },

  getCustomerPackagesManagement: async (storeId) => {
    const response = await api.get('/service-packages/customer-packages/management', withManagementStore(storeId));
    return (Array.isArray(response.data) ? response.data : []).map(normalizeCustomerServicePackageManagement);
  },

  sellInStore: async (storeId, { customerId, packageId, checkout }) => {
    const response = await api.post(
      '/service-packages/customer-packages/in-store',
      {
        customerId,
        packageId,
        paymentMethod: checkout?.paymentMethod,
        payments: checkout?.payments,
      },
      withManagementStore(storeId),
    );
    return normalizeCustomerServicePackage(response.data);
  },

  cancelCustomerPackage: async (storeId, customerServicePackageId) => {
    await api.post(
      `/service-packages/customer-packages/${customerServicePackageId}/cancel`,
      null,
      withManagementStore(storeId),
    );
  },

  getManagementBookingEntitlements: async (storeId, { customerId, serviceIds, date, appointmentId }) => {
    const response = await api.post(
      '/service-packages/management/booking-entitlements',
      {
        customerId,
        serviceIds,
        date: date || null,
        appointmentId: appointmentId || null,
      },
      withManagementStore(storeId),
    );

    return (response.data || []).map(normalizeBookingServicePackageEntitlement);
  },
};

export default ServicePackageService;
